/** D-70: only foreground activity renews the existing operational segment. */
/** @internal Exported only for its spec. */
export const OPERATIONAL_FOREGROUND_HEARTBEAT_MS = 5 * 60 * 1000;

export function createOperationalPresenceHeartbeat(input: Readonly<{
  foreground: () => boolean;
  renew: () => Promise<void>;
  onFailure: () => void;
}>) {
  let active = false;
  let generation = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pending = false;
  let dueAt = 0;
  const clear = () => { if (timer !== null) clearTimeout(timer); timer = null; };
  const schedule = () => {
    clear();
    if (!active || pending || !input.foreground()) return;
    const current = generation;
    timer = setTimeout(() => {
      timer = null;
      if (!active || current !== generation || !input.foreground()) return;
      pending = true;
      void Promise.resolve().then(() => {
        if (!active || current !== generation || !input.foreground()) return;
        return input.renew();
      }).then(() => {
        if (active && current === generation) {
          dueAt = Date.now() + OPERATIONAL_FOREGROUND_HEARTBEAT_MS;
          schedule();
        }
      }, () => {
        if (active && current === generation) { active = false; input.onFailure(); }
      }).finally(() => {
        pending = false;
        if (active && timer === null) schedule();
      });
    }, Math.max(0, dueAt - Date.now()));
  };
  return {
    start() { active = true; generation += 1; dueAt = Date.now() + OPERATIONAL_FOREGROUND_HEARTBEAT_MS; schedule(); },
    stop() { active = false; generation += 1; clear(); },
    foregroundChanged() { if (active) schedule(); },
  };
}
